import { Box, Flag, Skeleton, Stack, Typography } from '@packages/shared';
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useTracking } from '@packages/tracking';
import { graphql } from '@packages/gql/generated/shopping';
import { useQuery } from 'urql';
import { ServiceLayer } from '@packages/cms-components';
import { TextWithDialog } from '../TextWithDialog';
import { expressServiceInfoMessages } from './messages';

const ExpressServiceDocument = graphql(/* GraphQL */ `
  query ExpressServiceData {
    deliveryInfo {
      deliveryDate
      countdown {
        hours
        minutes
        seconds
      }
    }
  }
`);

/**
 * This component renders a delivery day and a countdown for express service delivery.
 */
export const ExpressServiceInfo = () => {
  const { formatMessage, locale } = useIntl();
  const dispatchGTMEvent = useTracking();

  const [result, reexecuteQuery] = useQuery({
    query: ExpressServiceDocument,
    context: useMemo(() => ({ suspense: false }), []),
  });

  const { deliveryInfo } = { ...result.data };
  const { deliveryDate, countdown } = { ...deliveryInfo };

  // Update the countdown on re-execute query
  useEffect(() => {
    if (result.fetching) return;

    // Set up to refetch every 60 seconds, if the query is idle
    const timerId = setTimeout(() => {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }, 60000);

    // Clear the timer to prevent memory leaks
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timerId);
  }, [result.fetching, reexecuteQuery]);

  if (!deliveryDate || !countdown) {
    return <Skeleton variant="rectangular" height={36} width="100%" />;
  }
  const deliveryDateObj = new Date(deliveryDate);
  const handleDialogClick = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: '24-Stunden-Lieferung',
    });
  };

  return (
    <Stack>
      <Typography variant="body2" sx={{ fontWeight: 'semiBold' }}>
        {formatMessage(expressServiceInfoMessages.expressServiceDeliveryUntil, {
          day: deliveryDateObj.toLocaleString(locale, { weekday: 'long' }),
        })}
      </Typography>
      <Box sx={{ typography: 'body2' }}>
        {formatMessage(expressServiceInfoMessages.expressServiceDeliveryCountdown, {
          countdown: (
            <Flag
              sx={{ backgroundColor: 'info.main', fontSize: 'body3' }}
              text={`${countdown.hours}:${countdown.minutes}h`}
            />
          ),
          modal: (msg: ReactNode) => (
            <TextWithDialog
              message={msg}
              dialogTitle={formatMessage(expressServiceInfoMessages.modalHeadline)}
              onClickHandler={handleDialogClick}
              variant="body2"
              dialogContent={<ServiceLayer serviceLayerId="delivery_24-stunden" />}
              displayTrackingProps={{ category: 'dv-service-layer-express-service-info' }}
            />
          ),
        })}
      </Box>
    </Stack>
  );
};
